import {InfiniteData} from '@tanstack/react-query';
import {useMemo} from 'react';
import useAssetsQuery from '@contentlab/hooks/use-assets-query';
import {Asset} from '@contentlab/types/asset';
import {AssetsQueryFilters} from '@/hooks/content-studio/use-assets-query/useAssetsQuery';

const DEFAULT_POLLING_INTERVAL = 3000;

interface UseOnAssetsReadyParams {
  assetIds: string[];
  onReady?: (assets: Asset[]) => void;
  pollingInterval?: number;
  keepPreviousData?: boolean;
}

function getAllAssetsReady(
  data: InfiniteData<Asset[]> | undefined,
  assetIds: string[]
) {
  const assets = data?.pages?.flatMap((page) => page) || [];

  const readyAssetsMap = new Map(
    assets
      .filter(
        (asset) => asset.status === 'ready' && assetIds.includes(asset.id)
      )
      .map((asset) => [asset.id, asset])
  );

  return assetIds.every((assetId) => readyAssetsMap.has(assetId));
}

function useReadyAssets({
  assetIds,
  onReady,
  pollingInterval = DEFAULT_POLLING_INTERVAL,
  keepPreviousData = true,
}: UseOnAssetsReadyParams) {
  const {data: allAssetUploads} = useAssetsQuery({
    sorting: {field: 'created_at', desc: true},
    filters: {
      type: 'uploads',
      ids: assetIds,
    } as AssetsQueryFilters,
    enabled: assetIds.length > 0,
    refetchInterval: (data) => {
      if (!data) {
        return false;
      }
      const allAssetsReady = getAllAssetsReady(data, assetIds);
      return allAssetsReady ? false : pollingInterval;
    },
    onSuccess: (data) => {
      const allAssetsReady = getAllAssetsReady(data, assetIds);
      if (allAssetsReady && onReady) {
        onReady(data?.pages?.flatMap((page) => page) || []);
      }
    },
    keepPreviousData,
  });

  return useMemo(() => {
    return allAssetUploads?.pages?.flatMap((page) => page) || [];
  }, [allAssetUploads]);
}

export default useReadyAssets;
